/**
 * AVISOCAR DOMAIN CONFIGURATION - PRODUCTION HARDENED
 * ====================================================
 * 
 * CANONICAL DOMAIN: https://www.avisocar.com
 * 
 * This file provides:
 * 1. Single source of truth for all domain/URL generation
 * 2. Canonical domain handling (www.avisocar.com)
 * 3. Backward compatibility for all QR formats
 * 4. Server-side QR URL generation (no window.location.origin)
 * 5. Environment-agnostic QR resolution
 */

// ===========================================
// CANONICAL DOMAIN CONFIGURATION
// ===========================================

// CANONICAL_DOMAIN: The official production domain
// ALL QR codes must use this domain (deliberate DOMAIN LOCK for printed QRs).
// Overridable via REACT_APP_PUBLIC_APP_URL for other deployments.
export const CANONICAL_DOMAIN = process.env.REACT_APP_PUBLIC_APP_URL || 'https://www.avisocar.com';
export const CANONICAL_HOST = CANONICAL_DOMAIN.replace(/^https?:\/\//, '').replace(/\/$/, '');

// APP_URL: Same as canonical domain for public links
export const APP_URL = CANONICAL_DOMAIN;

// BACKEND_URL: ALWAYS empty string = RELATIVE URLs
// This is the ROOT FIX for the recurring production failure.
// All API calls become /api/... (same-origin), which means:
// - On avisocar.com: requests go to avisocar.com/api/...
// - On preview: requests go to preview-url/api/...
// - Kubernetes ingress routes /api/ to backend port 8001
// - ZERO dependency on baked-in environment variables
// - IMMUNE to deployment URL changes or domain rebinding
export const BACKEND_URL = '';

// Legacy aliases for backward compatibility
export const PRODUCTION_DOMAIN = CANONICAL_DOMAIN;
export const APP_DOMAIN = CANONICAL_DOMAIN;

// ===========================================
// DOMAIN DETECTION HELPERS
// ===========================================

/**
 * Check if running on the canonical production domain
 */
export const isCanonicalDomain = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.host.toLowerCase();
  return host === 'www.avisocar.com' || host === 'avisocar.com';
};

/**
 * Check if running on any avisocar domain (with or without www)
 */
export const isAvisocarDomain = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.host.toLowerCase();
  return host.includes('avisocar.com');
};

/**
 * Check if running on preview/development
 */
export const isPreviewDomain = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.host.toLowerCase();
  return host.includes('emergentagent.com') || 
         host.includes('localhost') || 
         host.includes('127.0.0.1');
};

// Legacy alias
export const isProductionDomain = isCanonicalDomain;

// ===========================================
// URL GENERATION HELPERS (SERVER-SIDE SAFE)
// ===========================================

/**
 * Get the API base URL
 * ALWAYS uses the configured backend, NOT window.location
 */
export const getApiBaseUrl = () => {
  // Always use the configured backend URL
  // This ensures consistency across all environments
  return BACKEND_URL;
};

/**
 * Get the canonical QR base URL
 * ALWAYS returns the canonical domain, regardless of current host
 */
export const getQrBaseUrl = () => {
  return CANONICAL_DOMAIN;
};

/**
 * Get the canonical app URL
 * ALWAYS returns the canonical domain for public-facing links
 */
export const getAppUrl = () => {
  return CANONICAL_DOMAIN;
};

/**
 * Build a canonical QR URL for a given token
 * This is the ONLY function that should be used to generate QR URLs
 * 
 * @param {string} token - The QR token
 * @returns {string} The canonical QR URL
 */
export const buildQrPublicUrl = (token) => {
  if (!token) {
    console.error('buildQrPublicUrl called with empty token');
    return null;
  }
  // ALWAYS use canonical domain for QR URLs
  return `${CANONICAL_DOMAIN}/q/${token}`;
};

// Legacy alias
export const buildQrUrl = buildQrPublicUrl;

/**
 * Build a vehicle detail URL
 */
export const buildVehicleUrl = (vehicleId) => {
  return `${CANONICAL_DOMAIN}/v/${vehicleId}`;
};

// ===========================================
// CANONICAL REDIRECT HELPER
// ===========================================

/**
 * Check if current URL should redirect to canonical domain
 * Returns the canonical URL if redirect needed, null otherwise
 */
export const getCanonicalRedirectUrl = () => {
  if (typeof window === 'undefined') return null;
  
  const host = window.location.host.toLowerCase();
  
  // If already on canonical domain, no redirect needed
  if (host === CANONICAL_HOST) return null;
  
  // If on non-www avisocar.com, redirect to www
  if (host === 'avisocar.com') {
    return `https://www.avisocar.com${window.location.pathname}${window.location.search}`;
  }
  
  // Don't redirect from preview domains
  return null;
};

/**
 * Normalize a QR token URL to canonical format
 * Handles all legacy formats:
 * - /q/{token}
 * - /qr/{token}
 * - /qr?code={token}
 */
export const normalizeQrUrl = (url) => {
  if (!url) return null;
  
  try {
    const parsed = new URL(url, CANONICAL_DOMAIN);
    const path = parsed.pathname;
    
    // Extract token from various formats
    let token = null;
    
    // Format: /q/{token}
    if (path.startsWith('/q/')) {
      token = path.replace('/q/', '');
    }
    // Format: /qr/{token}
    else if (path.startsWith('/qr/')) {
      token = path.replace('/qr/', '');
    }
    // Format: /qr?code={token}
    else if (path === '/qr') {
      token = parsed.searchParams.get('code');
    }
    
    if (token) {
      return buildQrPublicUrl(token);
    }
  } catch (e) {
    console.error('Error normalizing QR URL:', e);
  }
  
  return null;
};

// ===========================================
// QR RESOLUTION HELPER
// ===========================================

/**
 * Resolve a QR token to vehicle data
 * Uses the production backend directly
 * 
 * @param {string} token - The QR token
 * @returns {Promise<object>} The vehicle data or error
 */
export const resolvePublicQr = async (token) => {
  if (!token) {
    return { is_active: false, error_type: 'invalid_token' };
  }
  
  try {
    const response = await fetch(`/api/public/qr/${token}`);
    const data = await response.json();
    
    // Log for debugging
    console.log('[QR Resolution]', {
      token: token.substring(0, 8) + '...',
      is_active: data.is_active,
      origin: typeof window !== 'undefined' ? window.location.origin : 'SSR'
    });
    
    return data;
  } catch (error) {
    console.error('[QR Resolution Error]', error);
    return { is_active: false, error_type: 'network_error' };
  }
};

// ===========================================
// INITIALIZATION LOGGING
// ===========================================

if (typeof window !== 'undefined') {
  console.log('='.repeat(50));
  console.log('AVISOCAR DOMAIN CONFIG V2 (RELATIVE URLs)');
  console.log('='.repeat(50));
  console.log('Current host:', window.location.host);
  console.log('Current origin:', window.location.origin);
  console.log('Canonical domain:', CANONICAL_DOMAIN);
  console.log('API mode: RELATIVE (/api/...)');
  console.log('Is canonical:', isCanonicalDomain());
  console.log('Is preview:', isPreviewDomain());
  console.log('='.repeat(50));
  
  // Check for canonical redirect
  const redirectUrl = getCanonicalRedirectUrl();
  if (redirectUrl) {
    console.log('Canonical redirect suggested:', redirectUrl);
  }
}

// ===========================================
// EXPORTS
// ===========================================

export default {
  CANONICAL_DOMAIN,
  CANONICAL_HOST,
  APP_URL,
  BACKEND_URL,
  PRODUCTION_DOMAIN,
  APP_DOMAIN,
  isCanonicalDomain,
  isAvisocarDomain,
  isPreviewDomain,
  isProductionDomain,
  getApiBaseUrl,
  getQrBaseUrl,
  getAppUrl,
  buildQrPublicUrl,
  buildQrUrl,
  buildVehicleUrl,
  getCanonicalRedirectUrl,
  normalizeQrUrl,
  resolvePublicQr,
};
